import { EthereumProvider } from '@walletconnect/ethereum-provider';
import { Api } from '@blink/components';

import { EthereumWallet } from './ethereumWallet';
import { WalletAddress } from '../../types';
import { NETWORK, NETWORK_CHAIN } from '../../constants/global';
import { SendTransactionProps } from './types';
import Logger from '../../utils/logger';
import { getRandomRpcUrlByWeight, parseWeightedRpcUrls } from '../rpcPool';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
type Provider = EthereumProvider;

interface RequestArguments {
    method: string;
    params?: unknown[] | object;
}

/** Wallet Connect class to provide all realisation of abstract wallet methods */
export class WalletConnect extends EthereumWallet {
    private provider: Provider;
    private readonly WC_PROJECT_ID = Api.WC_PROJECT_ID as string;

    constructor() {
        super();
    }

    public getProjectId(): string {
        return this.WC_PROJECT_ID;
    }

    public getProjectMetaData() {
        return {
            name: 'Arkis',
            description: 'Arkis Web3Wallet for WalletConnect',
            url: Api.WC_HOME_URL as string,
            icons: [`${Api.WC_HOME_URL}/logo192.svg` as string],
        };
    }

    public isAvailable(): boolean {
        return true;
    }

    public isWalletInProvider(): boolean {
        return false;
    }

    public getRpcMap(): any {
        const weightedRpcs = parseWeightedRpcUrls(Api.WC_ETHEREUM_HTTP_URL || '');
        const rpcUrl = getRandomRpcUrlByWeight(weightedRpcs);
        return Api.WC_ETHEREUM_HTTP_URL ? { [NETWORK_CHAIN.ETHEREUM_NETWORK]: rpcUrl } : {};
    }

    public async getProvider(): Provider {
        if (this.provider) {
            return this.provider;
        }

        this.provider = await EthereumProvider.init({
            projectId: this.getProjectId(),
            metadata: this.getProjectMetaData(),
            showQrModal: true,
            optionalChains: [Number(NETWORK_CHAIN.ETHEREUM_NETWORK)],
            rpcMap: this.getRpcMap(),
        });

        this.overrideRequestMethod();

        return this.provider;
    }

    private overrideRequestMethod(): void {
        if (!this.provider) return;

        const originalRequest = this.provider.request.bind(this.provider);

        this.provider.request = async (args: RequestArguments, expiry?: number) => {
            try {
                return await originalRequest(args, expiry);
            } catch (error: any) {
                const message = error?.message;

                Logger.error(message, error);

                throw error;
            }
        };
    }

    public async changeNetwork(network: NETWORK): Promise<void> {
        await super.changeNetwork(network);
    }

    public async connect(): Promise<WalletAddress[]> {
        const isConnected = await this.isConnected();
        const provider = await this.getProvider();
        if (isConnected && provider.accounts && provider.accounts?.length) {
            return provider.accounts;
        }
        await provider.connect();
        return provider.accounts;
    }

    public async disconnect(): Promise<void> {
        const provider = await this.getProvider();
        await provider.disconnect();
    }

    public async getChainId(): Promise<string> {
        const provider = await this.getProvider();
        return provider.chainId;
    }

    public async getLibrary(): Promise<Provider> {
        return await this.getProvider();
    }

    public async getNetwork(): Promise<NETWORK_CHAIN> {
        const provider = await this.getProvider();
        return provider.chainId;
    }

    public async isConnected(): Promise<boolean> {
        const provider = await this.getProvider();
        return provider.connected;
    }

    public async sendTransaction(data: SendTransactionProps): Promise<void> {
        const provider = await this.getProvider();
        await provider.request(data);
    }
}
